import type { JSONContent } from '@tiptap/core'
import type { NodeProps } from '@tiptap/static-renderer'
import { renderToReactElement } from '@tiptap/static-renderer'
import katex from 'katex'
import React, { forwardRef, memo, useCallback, useDeferredValue, useMemo, useState } from 'react'
import styled from 'styled-components'

import { getImageFullUrlInApplication } from '../../utils'
import { FilePreviewer, PreviewFile } from '../FilePreviewer'
import { getExtensions } from './helper'
import { RichTextContentCss } from './styles'
import type { TiptapConfig } from './types'

/** 从 static-renderer 的 JSONContent 递归收集所有 image 节点（无 editor 时用） */
function collectImagesFromJSONContent(content: JSONContent | null | undefined, appId?: string): PreviewFile[] {
    if (!content) return []
    const list: PreviewFile[] = []
    function walk(node: JSONContent) {
        if (node.type === 'image' && node.attrs?.src) {
            list.push({
                url: getImageFullUrlInApplication(appId, node.attrs.src),
                name: node.attrs.title ?? node.attrs.alt ?? '',
                type: 'image'
            })
        }
        node.content?.forEach?.(walk)
    }
    walk(content)
    return list
}

const StyledTiptapRenderer = styled.div`
    ${RichTextContentCss}
`

export interface TiptapRendererProps extends React.ComponentPropsWithoutRef<'div'> {
    value: null | JSONContent | undefined
    config?: TiptapConfig
}

export const TiptapRenderer = memo(
    forwardRef<HTMLDivElement, TiptapRendererProps>((props, ref) => {
        const { value, config, ...rest } = props

        const deferredValue = useDeferredValue(value)
        const deferredConfig = useDeferredValue(config)
        const allImages = useMemo(
            () => collectImagesFromJSONContent(deferredValue, deferredConfig?.image?.appId),
            [deferredValue, deferredConfig?.image?.appId]
        )

        const extensions = useMemo(() => getExtensions({ ...deferredConfig, readonly: true }), [deferredConfig])

        const content = useMemo(
            () =>
                deferredValue &&
                renderToReactElement({
                    content: deferredValue,
                    extensions,
                    options: {
                        nodeMapping: {
                            image: ctx => <ImageRenderer {...ctx} appId={deferredConfig?.image?.appId} allImages={allImages} />,
                            inlineMath: MathematicsRenderer
                        }
                    }
                }),
            [deferredValue, extensions, deferredConfig?.image?.appId, allImages]
        )

        return (
            <StyledTiptapRenderer ref={ref} {...rest}>
                <div className="tiptap ProseMirror">{content}</div>
            </StyledTiptapRenderer>
        )
    })
)

function ImageRenderer({ node, appId, allImages }: NodeProps & { appId?: string; allImages: PreviewFile[] }) {
    const { attrs } = node

    const [opened, setOpened] = useState(false)
    const [currentIndex, setCurrentIndex] = useState(0)

    const handleOpenPreview = useCallback(() => {
        const url = getImageFullUrlInApplication(appId, attrs.src)
        const index = allImages.findIndex(f => f.url === url)
        const resolvedIndex = index >= 0 ? index : 0
        setCurrentIndex(resolvedIndex)
        setOpened(true)
    }, [attrs.src, appId, allImages])

    return (
        <>
            <div
                style={{ textAlign: attrs.textAlign }}
                onClick={() => {
                    handleOpenPreview()
                }}
            >
                <img src={getImageFullUrlInApplication(appId, attrs.src)} loading="lazy" decoding="async" alt={attrs.alt ?? ''} />
            </div>
            <FilePreviewer
                fileList={allImages}
                defaultIndex={currentIndex}
                opened={opened}
                onClose={() => {
                    setOpened(false)
                }}
            />
        </>
    )
}

function MathematicsRenderer({ node }: NodeProps) {
    const html = katex.renderToString(node.attrs.latex, {
        throwOnError: false
    })

    return (
        <span
            className="tiptap-mathematics-render"
            data-type="inline-math"
            data-latex={node.attrs.latex}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    )
}
